(ns frontend.view.nev.holding.view
  (:require
   [frontend.actions.common :as common-actions]
   [potpuri.core :as p :refer [fn->]]
   [common.localization :as loc]
   [clojure.string :as string]
   [frontend.logging :as logging]
   [frontend.view.entity-viewers.header :as header]
   [frontend.view.field-templates :as templates]
   [frontend.view.nev.events :as events]
   [frontend.view.entity-viewers.modules.risk.event :as risk-event]
   [common.entity :as entity]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.not-found :as not-found]
   [frontend.routing :as routing]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.state.current-session :as session]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.syncing-data :as syncing]
   [frontend.db :as app-db]
   [frontend.view.entity-viewers.components :as nev-components]
   [ui.util :as uu]
   [frontend.view.entity-viewers.modules.associated-entities :as associated-entities]
   [common.schema.field :as field]
   [frontend.state.permissions :as f-permissions]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.view.entity-viewers.modules.risk.view :as risk-module]
   [frontend.tenant-customizations :as tenant-customizations]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]))

(defn toolbar-buttons
  [{:keys [entity entity-ids] :as _view-args}]
  (let [entity-id (:db/id entity)
        flagged? (contains? (:account/flagged-entities @session/account)
                            entity-id)]
    (concat
     [(zap-toolbar/dropdown-button
       {:icon      :fa-star
        :on-select (partial common-actions/toggle-flag entity flagged?)
        :ord       1
        :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})]

     [(when-not (:common/disabled? entity)
        (zap-toolbar/delete-button entity [:holdings-grid]))]

     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids entity-ids})])))

(defn header-top
  [{:keys [entity read-only?]}]
  [:div
   {:class "flex items-center gap-2"}
   [:span
    [:i.text-2xl
     {:class (uu/cm :fal (entity/default-icon entity))}]]
   [:div
    [nev-components/name-view
     {:entity     entity
      :read-only? read-only?}]]])

(def default-field-defs
  [{:fields       {0 {:field :holding/exit-plan}
                   1 {:field :holding/recent-status}}
    :field-layout :f-2-2}])

(defn header-board
  [{:keys [_entity-atom]}]
  (let [header-state (r/atom {:edit-layout? false})
        set-edit-layout! (fn [val] (swap! header-state assoc :edit-layout? val))]
    (fn [{:keys [entity-atom]}]
      (let [entity @entity-atom
            entity-id (:db/id entity)
            field-defs-path [:holding :header :field-defs]
            field-defs-timeout-id :holding-field-defs
            field-defs (or (events/timeout-value field-defs-timeout-id
                                                 @(r/track session/get-view-config field-defs-path))
                           default-field-defs)
            overrides nil
            selected-fields (->> field-defs
                                 (map :fields)
                                 (mapcat vals)
                                 (keep :field))
            remove-fields #{}
            holding-schema (templates/entity-type-template
                            {:entity-type     :entity.type/holding
                             :selected-fields selected-fields
                             :tenant-schema   @session/tenant-schema
                             :account         @session/account
                             :role            @session/role
                             :remove-fields   remove-fields
                             :overrides       overrides})
            asset-system-fields [:company/intelligence-company
                                 :company/fullcontact-company]
            remove-asset-fields asset-system-fields
            target-asset-schema
            (templates/holding-target-asset-template
             {:selected-fields (->> selected-fields
                                    (filter vector?)
                                    (filter (fn-> count (= 2)))
                                    (filter (fn-> first (= :holding/target-asset)))
                                    (map last))
              :remove-fields   remove-asset-fields
              :tenant          @session/tenant
              :tenant-schema   @session/tenant-schema
              :account         @session/account
              :role            @session/role
              :overrides       {:asset/name {:editor/type :editor.type/link
                                             :read-only?  true}}})

            schema (concat holding-schema
                           (->> target-asset-schema
                                (map (fn [[k fs]]
                                       (let [ref-attribute [:holding/target-asset k]]
                                         [ref-attribute
                                          (assoc fs :label (templates/target-asset-label fs))])))))

            ;;test all fields:
            #_#_field-defs
              (->> schema
                   (map first)
                   (partition-all 4 4)
                   (map (fn [fields]
                          (->> fields
                               (map-indexed (fn [i f]
                                              [i {:field f}])))))
                   (map (partial into {}))
                   (map (fn [fields]
                          {:fields       fields
                           :field-layout :f-1-1-1-1}))
                   vec)]
        [:div
         [header/header-board
          {:top              [header-top {:entity entity}]
           :preview-top      [header-top
                              {:entity     entity
                               :read-only? true}]
           :actions          (toolbar-buttons {:entity entity})
           :entity-id        entity-id
           :entity-atom      entity-atom
           :edit-layout?     (:edit-layout? @header-state)
           :set-edit-layout! set-edit-layout!
           :field-defs       field-defs
           :schema           schema
           :save-layout!     (fn [new-field-defs]
                               (events/save-field-defs! field-defs-timeout-id
                                                        field-defs-path
                                                        new-field-defs
                                                        (fn [])
                                                        (fn [err]
                                                          (logging/error
                                                           "Failed to save field layout"
                                                           err))))}]]))))

(defn new-header?
  []
  (and (session/has-feature-access? :feature/customizable-nev)
       @session/customizable-nev?))

(defn holding-view
  [{:keys [holding-id is-guest?] :as _view-args}]
  (let [initialized? (r/atom false)
        state-atom (r/cursor app-db/app-state [:views :nev :holding])
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [holding-id])]
    (r/create-class
     {:component-did-update
      (partial entity-utils/component-did-update
               :holding-id
               (partial events/clear-errors! error-atom notification-atom))
      :component-did-mount
      (fn [_]
        (entity-utils/reset-state! state-atom holding-id
                                   {:always-fetch? (session/new-grid-2023?)})
        (reset! initialized? true)
        (when (and (session/risks-access?)
                   (f-permissions/field-read-access? :holding/risks
                                                     :entity.type/holding
                                                     nil))
          (rf/dispatch [::risk-event/load-risks holding-id])))
      :component-will-unmount
      (fn [_]
        (events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [holding-id] :as view-args}]
        (let [holding (get @session/entities holding-id)
              target-asset-id (:holding/target-asset holding)
              target-asset (get @session/entities target-asset-id)
              target-asset-type (:entity/type target-asset)]
          (cond
            (not @initialized?)
            [syncing/view]
            (or (:common/disabled? holding)
                (:not-found @state-atom))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not holding)
                    (:common/disabled? holding))
              [:div " "]
              (let [tenant-schema @session/tenant-schema
                    tenant-has-funds? (seq @session/all-funds)
                    header-fields [:holding/exit-plan :holding/recent-status]
                    view-id :holding
                    module-collapsed-path (fn [module-id]
                                            [view-id :modules :module-config module-id
                                             :collapsed?])
                    show-event-pane? (or (not (session/event-pane-disabled?))
                                         (and (session/value-creation?)
                                              (= target-asset-type :entity.type/company)))]
                [:<>
                 (when-not (session/is-guest?)
                   [zap-toolbar/nev-toolbar
                    {:state           state-atom
                     :label           (partial zap-toolbar/toolbar-back
                                               [:holdings-grid]
                                               (loc/tr :back-to/holdings-grid))
                     :actions         (toolbar-buttons (assoc view-args
                                                              :entity
                                                              holding
                                                              :entity-ids
                                                              (remove nil?
                                                                      [holding-id
                                                                       (:holding/target-asset holding)])))
                     :dropdown-toggle [:span (loc/tr :actions)
                                       [:i.fas.fa-angle-down.ml-2]]
                     :dropdown-toggle-class-name
                     "zap-button zap-button--primary-btn actions-dropdown"}])
                 [:div.entity__container
                  (when @error-atom
                    [nev-components/error-bar error-atom])
                  (when @notification-atom
                    [nev-components/notification-bar notification-atom])

                  [:div.entity
                   [:div
                    {:class "w-full max-w-[1920px] mx-auto p-4"}
                    (if (new-header?)
                      [header-board {:entity-atom entity-atom}]
                      [:div
                       [nev-components/owner-stage-rating-module
                        {:entity-cursor     entity-atom
                         :view-state        view-state-atom
                         :entity            holding
                         :owner-attribute   :holding/owner
                         :members-attribute :common/team-members}]
                       [nev-components/header-module
                        {:component-state temp-atom
                         :view-state      view-state-atom
                         :related-tags-entity-id (:holding/target-asset holding)
                         :entity          holding
                         :selected-fields header-fields}]])
                    [:div.md:grid.grid-cols-10.gap-4
                     [:div
                      {:class (if-not show-event-pane?
                                "col-span-10"
                                "col-span-10 lg:col-span-5")}
                      [:div.entity-left
                       [modules/view
                        {:view-id  view-id
                         :ordering (get-in @session/view-config [view-id :modules :ordering])
                         :save-modules-order! (partial events/save-modules-order!
                                                       (:entity/type holding))
                         :modules
                         (concat
                          [{:module-id  :holding-information
                            :header     (loc/tr :nev-module/about-this-entity-type
                                                [(string/lower-case (loc/tr :entity.type/holding))])
                            ;;TODO uncomment when finished:
                            #_:header-tools
                            #_(when (session/is-admin?)
                                [:span.entity-editor-header-settings
                                 {:on-click (fn [e])}
                                 ;;open settings

                                 [:span (loc/tr :entity-editor/settings-label)]
                                 [:i.fal.fa-cog]])
                            :collapsed? (get-in @session/view-config
                                                (module-collapsed-path :holding-information))
                            :save-module-collapsed! (partial events/save-module-collapsed!
                                                             :entity.type/holding)
                            :content    [:div
                                         [entity-editor/view
                                          {:state-atom    state-atom
                                           :entity-id     holding-id
                                           :view-id       view-id
                                           :module-id     :holding-information
                                           :remove-fields (concat [:holding/name :holding/fund
                                                                   :common/network-drive :common/named-network-drives]
                                                                  (field/entity-tag-fields
                                                                   tenant-schema
                                                                   (:entity/type holding))
                                                                  header-fields)
                                           :ordering      (get-in
                                                           @session/view-config
                                                           [view-id :modules :module-config
                                                            :holding-information :ordering])}]]}
                           (when (and (session/risks-access?)
                                      (f-permissions/field-read-access? :holding/risks
                                                                        :entity.type/holding
                                                                        nil))
                             (risk-module/risk-module-config
                              {:module-label       (loc/tr :nev-module/holding-risks)
                               :module-id          :holding-risks
                               :view-id            view-id
                               :nev-entity-type    :entity.type/holding
                               :target-entity-type :entity.type/risk-holding
                               :save-collapsed-status! (r/partial events/save-module-collapsed!
                                                                  (:entity/type
                                                                   :entity.type/holding))
                               :entity-id          holding-id
                               :add-new-button?    (f-permissions/field-write-access?
                                                    :holding/risks
                                                    :entity.type/holding
                                                    nil)}))

                           (when (and (or (:holding/target-asset holding)
                                          (not is-guest?))
                                      (or
                                       (not (tenant-customizations/capman?))
                                       (not
                                        (tenant-customizations/capman-in-real-estate-pipeline?))))
                             (let [module-id :holding-asset
                                   asset-type (->> holding
                                                   :holding/target-asset
                                                   (get @session/entities)
                                                   :entity/type)
                                   order-config-path [view-id :modules :module-config module-id
                                                      asset-type :ordering]
                                   collapsed-path (module-collapsed-path module-id)
                                   collapsed? (get-in @session/view-config collapsed-path)]
                               (nev-components/entity-relation-module
                                (merge
                                 {:state-atom          state-atom
                                  :module-id           module-id
                                  :view-id             view-id
                                  :ordering            (get-in
                                                        @session/view-config
                                                        order-config-path)
                                  :order-config-path   order-config-path
                                  :module-label        (if (:holding/target-asset holding)
                                                         (loc/tr :nev-module/existing-target-asset
                                                                 [(loc/tr asset-type)])
                                                         (loc/tr :nev-module/oppo-target-asset))
                                  :entity              holding
                                  :forced-entity-types (session/holding-target-asset-types)
                                  :relationship-attribute :holding/target-asset
                                  :header-tools
                                  [:span.grow.flex.justify-end
                                   (when (and (not is-guest?)
                                              (:holding/target-asset holding)
                                              (not collapsed?)
                                              (not (session/is-read-only-user?)))
                                     [nev-components/remove-attribute-icon
                                      {:entity    holding
                                       :attribute :holding/target-asset}])

                                   (when-let [_city (some->> holding
                                                             :holding/target-asset
                                                             (get @session/entities)
                                                             :asset/city)]

                                     [button/secondary-open
                                      {:tooltip (loc/tr :open-address-in-google-maps)}
                                      [:i.fal.fa-map-marker-alt
                                       {:on-click (fn [_]
                                                    (let [asset (->> holding
                                                                     :holding/target-asset
                                                                     (get @session/entities))]
                                                      (.open
                                                       js/window
                                                       (str "https://www.google.com/maps/place/"
                                                            (:asset/street-address asset)
                                                            ","
                                                            (when-let [zip (:asset/zip-code asset)]
                                                              (str zip "+"))
                                                            (:asset/city asset)))))}]])]}))))

                           (when (and tenant-has-funds?
                                      (f-permissions/field-read-access? :holding/fund
                                                                        :entity.type/holding
                                                                        nil))
                             (let [module-id :holding-fund
                                   order-config-path [view-id :modules :module-config module-id
                                                      :ordering]
                                   collapsed-path (module-collapsed-path module-id)
                                   collapsed? (get-in @session/view-config collapsed-path)
                                   header-tools (when (and (:holding/fund holding)
                                                           (not collapsed?)
                                                           (not (session/is-read-only-user?)))
                                                  [:span.grow.flex.justify-end
                                                   [nev-components/remove-attribute-icon
                                                    {:entity    holding
                                                     :attribute :holding/fund}]])]
                               (nev-components/entity-relation-module
                                {:state-atom        state-atom
                                 :module-id         module-id
                                 :view-id           view-id
                                 :ordering          (get-in
                                                     @session/view-config
                                                     order-config-path)
                                 :order-config-path order-config-path
                                 :module-label      (loc/tr :nev-module/holding-fund)
                                 :entity            holding
                                 :header-tools      header-tools
                                 :relationship-attribute :holding/fund})))]

                          (let [exit-oppos (when (session/exit-process-management?)
                                             (->> @session/exit-opportunities
                                                  (filterv (fn [{:keys [exit-opportunity/target-holding]}]
                                                             (= target-holding
                                                                holding-id)))))]
                            (when (and (not (session/is-guest?))
                                       (session/exit-process-management?)
                                       (seq exit-oppos))
                              [(nev-components/association-module-config
                                {:module-label        (loc/tr
                                                       :nev-module/holding-target-in-exit-oppos)
                                 :module-id           :target-in-exit-oppos
                                 :view-id             view-id
                                 :nev-entity-type     :entity.type/holding
                                 :target-entity-type  :entity.type/exit-opportunity
                                 :associated-entities exit-oppos
                                 :save-collapsed-status! (partial events/save-module-collapsed!
                                                                  (:entity/type holding))
                                 :add-new-button?     false})]))

                          (when-not (session/is-guest?)
                            (associated-entities/modules
                             {:entity-id holding-id
                              :save-module-collapsed! events/save-module-collapsed!
                              :module-collapsed-path module-collapsed-path}))
                          [(when (and (not (session/is-mobile?))
                                      (f-permissions/field-read-access? :common/attachments
                                                                        :entity.type/holding
                                                                        nil))
                             (nev-components/attachments-module :holding
                                                                :holding-attachments
                                                                holding))])}]]]
                     (when show-event-pane?
                       [:div
                        {:class "col-span-10 lg:col-span-5"}
                        [event-pane/view
                         {:state       events-pane-atom
                          :entity-ids  (remove nil?
                                               [holding-id
                                                (:holding/target-asset holding)])
                          :module-args {:state-atom state-atom
                                        :view-id    view-id}}]])]]]]])))))})))

(defmethod routing/render-view :entity.type/holding
  [{:keys [route-params _view-params]}]
  (let [holding-id (-> route-params
                       :id
                       js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page holding"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "holding-viewer-id-" holding-id)}
     [holding-view {:holding-id holding-id}]]))
